import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  useTheme,
  Alert,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { fetchBrands, createBrand, updateBrand, deleteBrand } from "../api/productApi";
import ImageUploader from "../components/ImageUploader";

interface Brand {
  id: number;
  name: string;
  logo?: string;
  description?: string;
}

const BrandsPage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentBrand, setCurrentBrand] = useState<Brand | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  useEffect(() => {
    const loadBrands = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchBrands();
        setBrands(data);
      } catch (error) {
        console.error("Failed to load brands:", error);
        setError("Failed to load brands. Please try again later.");
        enqueueSnackbar("Failed to load brands", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    loadBrands();
  }, [enqueueSnackbar]);

  const handleOpenCreateDialog = () => {
    setCurrentBrand({ id: 0, name: "", logo: "", description: "" });
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (brand: Brand) => {
    setCurrentBrand(brand);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentBrand(null);
  };

  const handleSaveBrand = async () => {
    if (!currentBrand || !currentBrand.name) {
      enqueueSnackbar("Brand name is required", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      if (isEditing && currentBrand.id) {
        const updatedBrand = await updateBrand(currentBrand.id, {
          name: currentBrand.name,
          logo: currentBrand.logo,
          description: currentBrand.description
        });
        setBrands(brands.map(b => b.id === updatedBrand.id ? updatedBrand : b));
        enqueueSnackbar("Brand updated successfully", { variant: "success" });
      } else {
        const newBrand = await createBrand({
          name: currentBrand.name,
          logo: currentBrand.logo,
          description: currentBrand.description
        });
        setBrands([...brands, newBrand]);
        enqueueSnackbar("Brand created successfully", { variant: "success" });
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error("Error saving brand:", error);
      const errorMessage = error.response?.data?.error || 
                         error.message || 
                         "Failed to save brand";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this brand?")) {
      return;
    }

    try {
      setLoading(true);
      await deleteBrand(id);
      setBrands(brands.filter(brand => brand.id !== id));
      enqueueSnackbar("Brand deleted successfully", { variant: "success" });
    } catch (error: any) {
      console.error("Error deleting brand:", error);
      const errorMessage = error.response?.data?.error || 
                         "Failed to delete brand. It may be associated with existing products.";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (loading && brands.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Brand Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
          disabled={loading}
        >
          Add Brand
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="120px">Logo</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell width="120px" align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brands.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No brands found
                    </TableCell>
                  </TableRow>
                ) : (
                  brands.map((brand) => (
                    <TableRow key={brand.id} hover>
                      <TableCell>
                        {brand.logo ? (
                          <img
                            src={brand.logo}
                            alt={brand.name}
                            style={{ 
                              maxHeight: 50, 
                              maxWidth: 100,
                              objectFit: 'contain'
                            }}
                          />
                        ) : (
                          <Box 
                            width={100} 
                            height={50} 
                            bgcolor={theme.palette.grey[200]}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography variant="caption">No logo</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {brand.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {brand.description ? (
                          <>
                            {brand.description.substring(0, 50)}
                            {brand.description.length > 50 && "..."}
                          </>
                        ) : (
                          <Typography color="textSecondary">No description</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleOpenEditDialog(brand)}
                          color="primary"
                          disabled={loading}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteBrand(brand.id)}
                          color="error"
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>
          {isEditing ? "Edit Brand" : "Create New Brand"}
        </DialogTitle>
        <DialogContent dividers>
          {currentBrand && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{xs: 12}}>
                <TextField
                  label="Brand Name"
                  fullWidth
                  value={currentBrand.name}
                  onChange={(e) =>
                    setCurrentBrand({ ...currentBrand, name: e.target.value })
                  }
                  margin="normal"
                  required
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <Typography variant="subtitle2" gutterBottom>
                  Brand Logo
                </Typography>
                <ImageUploader
                  imageUrl={currentBrand.logo || ""}
                  onImageChange={(url) =>
                    setCurrentBrand({ ...currentBrand, logo: url })
                  }
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={currentBrand.description || ""}
                  onChange={(e) =>
                    setCurrentBrand({ ...currentBrand, description: e.target.value })
                  }
                  margin="normal"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog} 
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveBrand}
            variant="contained"
            disabled={loading || !currentBrand?.name}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandsPage;